import { useAppSelector } from "@app/store";
import Bank from "@assets/icons/bank.svg";
import CardTick from "@assets/icons/card-tick.svg";
import Copy from "@assets/icons/copy.svg";
import DocumentText from "@assets/icons/document-text.svg";
import Edit from "@assets/icons/edit.svg";
import Mobile from "@assets/icons/mobile.svg";
import { Block } from "@components/block";
import { Breadcrumbs } from "@components/breadcrumbs";
import { Button } from "@components/button";
import { Card } from "@components/card";
import { BaseDevice } from "@components/devices/device";
import { BaseInput } from "@components/input";
import { FlexLoader, GlobalLoader } from "@components/loader";
import { Modal } from "@components/modal";
import { RemoveRequisiteConfirm } from "@components/remove-requisite-confirm";
import { StatusBlock, StatusBlockType } from "@components/status-block";
import { Config, DoubleText, Table } from "@components/table";
import { useOrders } from "@features/order";
import { Order } from "@features/order/api";
import { useRequisites } from "@features/requisite";
import { DetailRequisite, getRequisite } from "@features/requisite/api";
import { formatDate, formatTime } from "@helpers/date";
import { formatRubles, formatUsdt } from "@helpers/format";
import {
  copyToClipboard,
  formatSeconds,
  withErrorHandling,
} from "@helpers/utilities";
import { useCountdown } from "@hooks/use-countdown";
import { useNotification } from "@hooks/use-notification";
import { usePagination } from "@hooks/use-pagination";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./detail.module.scss";
import { EllipsisText } from "@components/ellipsis-text";
import { TraderDetailedOrderModal } from "@components/traderDetailedOrderModal";
import { useBoolState } from "@hooks/use-bool-state";
import { useAuth } from "@features/auth";
import { useWebSocket } from "@request/useWebsocket";
import { WebsocketUpdateType } from "@request/websocket";

const config: Config<Order> = [
  {
    label: "Номер заявки",
    source: "id",
    renderComponent: (data) => <EllipsisText text={data} copy />,
  },
  {
    label: "Время",
    source: "created_at_unix",
    renderComponent: (dateTime) => formatTime(new Date(dateTime * 1000)),
  },
  {
    label: "Дата",
    source: "created_at_unix",
    renderComponent: (dateTime) => formatDate(new Date(dateTime * 1000)),
  },
  {
    label: "Сумма заявки",
    source: "order_start_amount",
    renderComponent: (cellData, item) => {
      const usdt = formatUsdt(item.order_start_amount_usdt);
      const rubles = formatRubles(cellData);
      return <DoubleText text={rubles} subText={usdt} />;
    },
  },
  {
    label: "Измененная сумма",
    source: "merchant_amount",
    renderComponent: (data, item) => {
      if (item.merchant_amount_usdt == null || data == null) return "";
      if (item.merchant_amount_usdt == 0 || data == 0) return "";
      const usdt = formatUsdt(item.merchant_amount_usdt);
      const rubles = formatRubles(data);

      return <DoubleText subText={usdt} text={rubles} />;
    },
  },
  {
    label: "Итоговая сумма",
    source: "order_final_amount",
    renderComponent: (cellData, item) => {
      const usdt = formatUsdt(item.order_final_amount_usdt);
      const rubles = formatRubles(cellData);
      return <DoubleText text={rubles} subText={usdt} />;
    },
  },
  {
    label: "Статус",
    source: "status",
    renderComponent: (status) => {
      let text = "";
      let statusType: StatusBlockType;
      switch (status) {
        case "completed":
          text = "Закрыта";
          statusType = StatusBlockType.Success;
          break;
        case "canceled":
          text = "Отменена";
          statusType = StatusBlockType.Error;
          break;
        case "processing":
        default:
          text = "Ожидает подтверждения";
          statusType = StatusBlockType.Warning;
          break;
      }
      return <StatusBlock text={text} type={statusType} />;
    },
  },
];

export const DetailPage = () => {
  const navigate = useNavigate();
  const { detailId } = useParams();
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const [isModalOpen, showModal, hideModal] = useBoolState(false);
  const { isAdmin } = useAuth();

  const allowFarmCreation = useAppSelector(
    (state) => state.auth.allowFarmCreation
  );
  const allowRequisitesWithoutDevice = useAppSelector(
    (state) => state.auth.allowRequisitesWithoutDevice
  );

  const [selectedCheckDetail, setSelectedCheckDetail] =
    useState<DetailRequisite>();
  const resetSelectedCheckDetail = useCallback(() => {
    setSelectedCheckDetail(undefined);
  }, []);

  const [selectedRemoveDetailId, setSelectedRemoveDetailId] =
    useState<string>();
  const resetSelectedRemoveDetailId = useCallback(() => {
    setSelectedRemoveDetailId(undefined);
  }, []);

  const {
    state: requisiteState,
    getRequisite,
    enableRequisite,
  } = useRequisites();
  const requisite = requisiteState.requisite;
  const { state, getRequisiteOrders } = useOrders();
  const { page, limit, setPage, setLimit } = usePagination();

  const { lastMessage } = useWebSocket();

  useEffect(() => {
    if (
      lastMessage &&
      lastMessage.type == WebsocketUpdateType.Order &&
      detailId
    ) {
      getRequisiteOrders(detailId, { page, limit });
    }
  }, [lastMessage, limit, page, detailId]);

  useEffect(() => {
    if (detailId != null) getRequisiteOrders(detailId, { page: 1, limit: 10 });
  }, [detailId]);
  // useEffect(() => {
  //   let intervalId: NodeJS.Timeout | null = null;
  //   if (detailId) {
  //     const fetchData = () => {
  //       getRequisiteOrders(detailId, { page, limit });
  //     };
  //     fetchData();
  //     intervalId = setInterval(fetchData, 30000);
  //   }
  //   return () => {
  //     intervalId && clearInterval(intervalId);
  //   };
  // }, [detailId, getRequisiteOrders, limit, page]);

  useEffect(() => {
    if (detailId) {
      getRequisite(detailId);
    }
  }, [detailId, getRequisite]);

  const isCheckDisabled = useMemo(() => {
    if (!requisite) {
      return true;
    }

    if (!requisite.device_id && !allowRequisitesWithoutDevice) {
      return true;
    }

    if (
      !requisite.type ||
      !requisite.full_name ||
      !requisite.phone_number ||
      !requisite.card_number ||
      (requisite.type === "ACCOUNT" && !requisite.account_number)
    ) {
      return true;
    }

    if (
      allowFarmCreation &&
      (!requisite.start_time ||
        !requisite.end_time ||
        !requisite.max_amount_per_period ||
        !requisite.max_transaction ||
        !requisite.min_interval ||
        !requisite.min_transaction ||
        !requisite.max_transaction ||
        !requisite.transaction_count)
    ) {
      return true;
    }

    return false;
  }, [allowFarmCreation, allowRequisitesWithoutDevice, requisite]);

  const method = useMemo(() => {
    switch (requisite?.type) {
      case "SBP":
        return "СБП";
      case "ACCOUNT":
        return "Счет";
      case "CARD":
      default:
        return "Карта";
    }
  }, [requisite?.type]);

  const number = useMemo(() => {
    switch (requisite?.type) {
      case "CARD":
        return requisite.card_number;
      case "ACCOUNT":
        return requisite.account_number;
      case "SBP":
      default:
        return requisite?.phone_number;
    }
  }, [
    requisite?.account_number,
    requisite?.card_number,
    requisite?.phone_number,
    requisite?.type,
  ]);

  if (requisiteState.loading || !requisite) {
    return <FlexLoader />;
  }

  return (
    <>
      <div className={styles.container}>
        <Breadcrumbs items={["Реквизиты", number!]} />
        <Block className={styles.detail}>
          <div className={styles.left}>
            <Card
              active={requisite.active}
              atWork={requisite.at_work}
              deleted={requisite.deleted}
              balance={requisite.balance}
              bankName={requisite.bank.name}
              name={requisite.full_name}
              number={number}
              className={styles.card}
              scheduledActive={requisite.scheduled_active}
              tooltip={requisite.scheduler_tooltip}
            />
            <div className={styles.leftBottom}>
              <div className={styles.blockWithTitle}>
                <p className={styles.title}>Метод</p>
                <p className={styles.value}>{method}</p>
              </div>
              <div className={styles.blockWithTitle}>
                <p className={styles.title}>Конверсия</p>
                <p className={styles.value}>
                  {Math.round((requisite.conversion || 0) * 100) / 100}%
                </p>
              </div>
            </div>
          </div>
          <div className={styles.right}>
            {!requisite.deleted && (
              <div className={styles.headerRight}>
                <div className={styles.buttons}>
                  {!requisite.active && (
                    <Button
                      type="outline"
                      className={styles.stopButton}
                      disabled={isCheckDisabled}
                      onClick={() => setSelectedCheckDetail(requisite)}
                    >
                      Проверка реквизита
                    </Button>
                  )}
                  <Button
                    type="outline"
                    className={styles.toggleButton}
                    disabled={!requisite.active}
                    onClick={() =>
                      enableRequisite(requisite.id, {
                        at_work: !requisite.at_work,
                      })
                    }
                  >
                    {requisite.at_work ? "Остановить" : "Запустить"}
                  </Button>
                  <Button
                    type="outline"
                    className={styles.removeButton}
                    disabled={requisite.at_work}
                    onClick={() => setSelectedRemoveDetailId(requisite.id)}
                  >
                    В архив
                  </Button>
                </div>
                <Button
                  className={styles.editButton}
                  iconClassName={styles.editButtonIcon}
                  icon={Edit}
                  onClick={() => navigate("edit")}
                >
                  Редактировать
                </Button>
              </div>
            )}
            {allowFarmCreation || isAdmin ? (
              <div className={styles.rightBottom}>
                <div className={styles.rightBottomLeft}>
                  <BaseDevice
                    className={styles.device}
                    name={requisite.device_name}
                    status="select"
                  />
                  <BaseInput
                    disabled
                    value={requisite.transaction_count}
                    label="Количество сделок за период"
                    postNote="шт"
                    className={styles.input}
                  />
                  <div className={styles.twoInputs}>
                    <p className={styles.title}>Суммы сделок</p>
                    <div className={styles.inputs}>
                      <BaseInput
                        disabled
                        value={requisite.min_transaction}
                        label="Минимальная"
                        postNote="руб"
                        className={styles.input}
                      />
                      <BaseInput
                        disabled
                        value={requisite.max_transaction}
                        label="Максимальная"
                        postNote="руб"
                        className={styles.input}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.rightBottomRight}>
                  <BaseInput
                    className={classNames(styles.input, styles.interval)}
                    disabled
                    value={requisite.min_interval}
                    label="Минимальный интервал между сделками"
                    postNote="сек"
                  />
                  <BaseInput
                    disabled
                    value={requisite.max_amount_per_period}
                    label="Максимальная сумма за период"
                    postNote="руб"
                    className={styles.input}
                  />
                  <div style={{marginTop: '10px'}}>
                    <input
                      type="checkbox"
                      disabled
                      checked={
                        requisiteState.requisite?.intrabank_only ?? false
                      }
                    />
                    <label>Внутрибанковский режим (IBT)</label>
                  </div>
                  <div className={styles.twoInputs}>
                    <p className={styles.title}>
                      Время работы реквизита <small>(UTC+3 МСК)</small>
                    </p>
                    <div className={styles.inputs}>
                      <BaseInput
                        disabled
                        value={requisite.start_time}
                        label="Начало"
                        className={styles.input}
                      />
                      <BaseInput
                        disabled
                        value={requisite.end_time}
                        label="Конец"
                        className={styles.input}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <BaseDevice
                className={styles.device}
                name={requisite.device_name}
                status="select"
              />
            )}
          </div>
        </Block>
        <Block>
          <Table
            title="Заявки по реквизиту"
            emptyText="Нет заявок"
            config={config}
            data={state.requisiteOrders}
            loading={state.requisiteOrdersLoading}
            setPage={setPage}
            setLimit={setLimit}
            page={page}
            limit={limit}
            totalCount={state.requisiteOrdersTotalCount}
            onRowClick={(item) => {
              setDetailedOrder(item);
              showModal();
            }}
          />
        </Block>
      </div>
      <RemoveRequisiteConfirm
        hide={resetSelectedRemoveDetailId}
        requisiteId={selectedRemoveDetailId}
      />
      <CheckDetailModal
        detail={selectedCheckDetail}
        onHide={resetSelectedCheckDetail}
      />
      {requisiteState.isEnabling && <GlobalLoader />}

      {detailedOrder != null && (
        <TraderDetailedOrderModal
          isOpen={isModalOpen}
          hide={hideModal}
          order={detailedOrder}
        />
      )}
    </>
  );
};

type CheckDetailModalProps = {
  detail?: Pick<
    DetailRequisite,
    "id" | "bank" | "account_number" | "card_number" | "phone_number" | "type"
  >;
  onHide: () => void;
};

const CheckDetailModal = ({ detail, onHide }: CheckDetailModalProps) => {
  if (!detail) {
    return null;
  }

  return (
    <Modal className={styles.modal} isOpen={true} onClose={onHide}>
      <ModalContent detail={detail} onClose={onHide} />
    </Modal>
  );
};

const ModalContent = ({
  detail,
  onClose,
}: Required<Pick<CheckDetailModalProps, "detail">> & {
  onClose: () => void;
}) => {
  const [sum, setSum] = useState<number>();
  const [expiredAt, setExpiredAt] = useState<number>();

  const initialTime = expiredAt
    ? Math.max(
        0,
        Math.ceil((new Date(expiredAt * 1000).getTime() - Date.now()) / 1000)
      )
    : 0;

  const type = useMemo(() => {
    switch (detail.type) {
      case "CARD":
        return "card_transfer";
      case "ACCOUNT":
        return "account_transfer";
      case "SBP":
      default:
        return "sbp";
    }
  }, [detail.type]);

  const number = useMemo(() => {
    switch (detail.type) {
      case "CARD":
        return detail.card_number;
      case "ACCOUNT":
        return detail.account_number;
      case "SBP":
      default:
        return detail.phone_number;
    }
  }, [
    detail.account_number,
    detail.card_number,
    detail.phone_number,
    detail.type,
  ]);

  const [isLoading, setIsLoading] = useState(true);
  const notif = useNotification();
  const [error, setError] = useState("");

  const { activateRequisite, getRequisite: getRequisiteFromState } =
    useRequisites();

  useEffect(() => {
    let isMounted = true;
    const checkStatus = () => {
      getRequisite(detail.id).then((data) => {
        if (isMounted && data.data.active) {
          setIsLoading(false);
          onClose();
          notif.addNotification("Реквизит подтвержден");
          getRequisiteFromState(detail.id);
        }
        if (isMounted) {
          setTimeout(checkStatus, 3000);
        }
      });
    };

    checkStatus(); // Start polling immediately

    return () => {
      isMounted = false; // Cleanup: prevent further actions
    };
  }, []);

  useEffect(() => {
    withErrorHandling(
      async () => {
        const res = await activateRequisite({
          requisite: detail.id,
          type_confirm: type,
        });

        setSum(res.amount);
        setExpiredAt(res.time_remaining);
      },
      setError,
      () => setIsLoading(false)
    );
  }, [activateRequisite, detail.id, type]);

  if (isLoading) {
    return <FlexLoader style={{ width: "200px", height: "200px" }} />;
  }

  if (error) {
    return <span>{error || "Произошла ошибка"}</span>;
  }

  return (
    <>
      <p className={styles.title}>Проверка реквизита</p>
      <p className={styles.subTitle}>
        Необходимо убедиться в верности введенных данных
      </p>
      <p className={styles.actionText}>
        Отправьте <span className={styles.sum}>{sum} Руб</span> по номеру
        телефона:
      </p>
      <div
        className={classNames(styles.block, styles.phone)}
        onClick={() => copyToClipboard(number)}
      >
        <div className={styles.iconWithText}>
          <div className={styles.icon}>
            {detail.type === "CARD" && <CardTick />}
            {detail.type === "ACCOUNT" && <DocumentText />}
            {detail.type === "SBP" && <Mobile />}
          </div>
          <span className={styles.text}>{number}</span>
        </div>
        <div className={styles.copy}>
          <Copy />
        </div>
      </div>
      <div className={classNames(styles.block, styles.bank)}>
        <div className={styles.iconWithText}>
          <div className={styles.icon}>
            <Bank />
          </div>
          <span className={styles.text}>{detail.bank.name}</span>
        </div>
      </div>
      <div className={styles.timeWrapper}>
        <span className={styles.text}>Время на отправку:</span>
        <div className={styles.timeWithRadio}>
          {initialTime && <Timer initialTime={initialTime} />}
          <div className={styles.radio} />
        </div>
      </div>

      <Button disabled>Ожидаем СМС или Push...</Button>
    </>
  );
};

const Timer = ({ initialTime }: { initialTime: number }) => {
  const remainingSeconds = useCountdown(initialTime);

  return <span className={styles.time}>{formatSeconds(remainingSeconds)}</span>;
};
